import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { inject, observer } from 'mobx-react';
import { CSSTransition } from 'react-transition-group';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { RootStore } from '@/models';
import s from './topPanel.less';
import { Icon, Switch, Slider } from '@/components';
import { MainChannel } from '@/models/simultaneousInterpretation';

interface ModelState {
  RTCInstance?: RootStore['rtc'];
  simultaneous?: RootStore['simultaneousInterpretation'];
  meeting?: RootStore['meeting'];
}

interface Props extends ModelState {
  children: Element | ReactNode;
  next: () => void;
  visible: boolean;
  show: () => void;
  hide: () => void;
}

const SwitchChannelH5: React.FC<Props> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    meeting: store.meeting,
    simultaneous: store.simultaneousInterpretation,
  };
})(
  observer((props) => {
    const { show, hide, simultaneous, meeting, children, next, visible } = props;
    const { info } = meeting || {};
    const { meetingId, channelId } = info || {};
    const { isReceiveMainChannel, currentChannelItem, isMainChannel, mainChannelVolume } =
      simultaneous || {};
    const { formatMessage } = useIntl();

    const onClick = () => {
      show();
    };

    return (
      <>
        {children &&
          // @ts-ignore
          React.cloneElement(children, {
            onClick,
          })}
        {ReactDOM.createPortal(
          <div className={s.topPanel}>
            <CSSTransition
              in={visible}
              timeout={100}
              classNames={{
                enter: s.enter,
                enterActive: s.enterActive,
                enterDone: s.enterDone,
                exit: s.exit,
                exitActive: s.exitActive,
                exitDone: s.exitDone,
              }}
            >
              <div className={s.container}>
                <div className={s.top}>
                  <Icon
                    className={s.close}
                    name="icon-close_nor"
                    onClick={() => {
                      hide();
                    }}
                  />
                  <span>{formatMessage({ id: 'INTERPRETATION' })}</span>
                </div>
                <div
                  className={s.panel}
                  onClick={(e) => {
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                >
                  <div className={s.block}>
                    <div
                      className={s.item}
                      onClick={() => {
                        next && next();
                        show();
                      }}
                    >
                      <div className={s.desc}>{formatMessage({ id: 'AUDIENCE_CHANNEL' })}</div>
                      <div className={s.right}>
                        {currentChannelItem?.languageName}
                        <Icon name="icon-a-16_outlined_right" />
                      </div>
                    </div>
                  </div>
                  {!isMainChannel && (
                    <div className={classNames(s.block, s.muteBlock)}>
                      <div className={s.mute}>
                        <div className={s.desc}>
                          {formatMessage({ id: 'RECEIVE_MAIN_CHANNEL' })}
                        </div>
                        <Switch
                          checked={isReceiveMainChannel}
                          onChange={() =>
                            simultaneous?.receiveMainChannel(!simultaneous?.isReceiveMainChannel)
                          }
                        >
                          {formatMessage({ id: 'RECEIVE_MAIN_CHANNEL' })}
                        </Switch>
                      </div>
                    </div>
                  )}
                  {isReceiveMainChannel && !isMainChannel && (
                    <div className={s.voiceBlock}>
                      <div className={s.title}>{formatMessage({ id: 'VOLUME' })}</div>
                      <div className={s.box}>
                        <div className={s.info}>
                          <div className={s.desc}>
                            {formatMessage({ id: 'MAIN_CHANNEL_VOICE_H5' })}
                          </div>
                          <span className={s.voiceNum}>{mainChannelVolume}</span>
                        </div>
                        <div className={s.voiceChange}>
                          <Icon name="icon-speaker_low" />
                          <Slider
                            tooltipVisible={false}
                            className={s.mainChannelVoiceSlider}
                            defaultValue={mainChannelVolume}
                            onChange={(val) => {
                              simultaneous?.setMainChannelVolume(val);
                            }}
                          />
                          <Icon name="icon-speaker_high" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CSSTransition>
          </div>,
          document.body
        )}
      </>
    );
  })
);
export default SwitchChannelH5;
